import { Injectable, computed } from '@angular/core';
import { Router } from '@angular/router';
import {
  Subject,
  debounceTime,
  takeUntil,
  Observable,
  catchError,
  tap,
  finalize,
  of,
} from 'rxjs';
import { UserRights } from '@maximizer/core/shared/domain';
import {
  EventModule,
  EventJourney,
  EventAction,
  CaseCard,
  OpportunityCard,
  EntryType,
  EventMetadata,
} from '@maximizer/outlook/shared/domain';
import {
  TrackerService,
  OutlookService,
  EmailService,
  ComposeStore,
  OutlookStore,
  VerticalService,
} from '@maximizer/outlook/shared/data-access';
import { OpportunityService } from '@maximizer/outlook/opportunity/data-access';
import { CaseService } from '@maximizer/outlook/case/data-access';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class RelatedService {
  constructor(
    private readonly tracker: TrackerService,
    private readonly opportunityService: OpportunityService,
    private readonly caseService: CaseService,
    private readonly outlookService: OutlookService,
    private readonly emailService: EmailService,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly verticalService: VerticalService,
    private readonly outlookStore: OutlookStore,
    private readonly composeStore: ComposeStore,
  ) {}

  get isComposeMode(): boolean {
    return !!this.outlookService.isCompose;
  }

  canShowRelatedSection(userRights: UserRights | undefined): boolean {
    return !!(
      userRights?.opportunities?.read || userRights?.customerService?.read
    );
  }

  canShowComposeRelatedSection(userRights: UserRights | undefined): boolean {
    return (
      !!userRights?.documents?.insert && this.canShowRelatedSection(userRights)
    );
  }

  getRelatedTitle(userRights: UserRights | undefined): string {
    const opportunityLabel = this.translate.instant(
      'outlook.entries.opportunities',
    );
    const caseLabel = this.verticalService.translateVertical('cases');

    if (userRights?.customerService?.read && userRights?.opportunities?.read) {
      return this.translate.instant('outlook.main.related.fullTitle', {
        oppLabel: opportunityLabel,
        caseLabel,
      });
    }

    return this.translate.instant('outlook.main.related.halfTitle', {
      label: userRights?.customerService?.read ? caseLabel : opportunityLabel,
    });
  }

  navigateToRelated(): void {
    this.tracker.trackEvent({
      module: EventModule.RelatedRecords,
      journey: EventJourney.Open,
      action: EventAction.Clicked,
      metadata: { location: this.isComposeMode ? 'compose' : 'read' },
    });

    this.outlookStore.clearRelatedSearchContext();
    this.router.navigate(['related']);
  }

  // Signal producers are read before any early return so the computed always
  // tracks them; a null-item evaluation would otherwise cache [] forever.
  readonly relatedEntryKeys = computed<string[]>(() => {
    const readEntryKeys = this.outlookStore.inMaximizerIds();
    const savedToEntries = [
      ...this.composeStore.inMaximizer(),
      ...this.composeStore.saveOnlyEntries(),
    ];

    if (!this.outlookService.mailboxItem) {
      return [];
    }

    if (!this.isComposeMode) {
      return readEntryKeys;
    }

    // Leads are excluded: opportunities and cases match on AbEntryKey/ContactKey only
    const addressBookKeys = savedToEntries
      .filter((savedToEntry) => savedToEntry.type === EntryType.AbEntry)
      .flatMap((savedToEntry) => [savedToEntry.id, savedToEntry.parentKey])
      .filter((key): key is string => !!key);

    return [...new Set(addressBookKeys)];
  });

  setupSearchSubject(
    searchSubject: Subject<string>,
    destroy$: Subject<void>,
    onSearch: (term: string) => void,
    minLength = 3,
  ): void {
    searchSubject
      .pipe(debounceTime(300), takeUntil(destroy$))
      .subscribe((searchTerm) => {
        const trimmedSearchTerm = searchTerm.trim();

        if (trimmedSearchTerm.length >= minLength) {
          onSearch(trimmedSearchTerm);
        }
      });
  }

  searchOpportunities(searchTerm: string): Observable<OpportunityCard[]> {
    let isSuccessful = false;

    return this.opportunityService.search(searchTerm, true, 15).pipe(
      tap(() => {
        isSuccessful = true;
      }),
      catchError((error) => {
        console.error('Search error for opportunities:', error);
        return of([]);
      }),
      finalize(() => {
        this.trackRelatedSearchComplete(isSuccessful, EventModule.Opportunity);
      }),
    );
  }

  searchCases(searchTerm: string): Observable<CaseCard[]> {
    let isSuccessful = false;

    return this.caseService.search(searchTerm, true, 15).pipe(
      tap(() => {
        isSuccessful = true;
      }),
      catchError((error) => {
        console.error('Search error for cases:', error);
        return of([]);
      }),
      finalize(() => {
        this.trackRelatedSearchComplete(isSuccessful, EventModule.Case);
      }),
    );
  }

  trackRelatedSearchComplete(isSuccessful: boolean, module: EventModule): void {
    this.tracker.trackEvent({
      module: EventModule.RelatedRecords,
      journey: EventJourney.Search,
      action: EventAction.Completed,
      metadata: {
        object: module,
        success: isSuccessful,
      },
    });
  }

  async getSavedEmails(): Promise<{
    opportunities: string[];
    cases: string[];
  }> {
    try {
      return await this.outlookService.getSavedEmails();
    } catch (error) {
      console.error('Error retrieving saved emails:', error);
      return { opportunities: [], cases: [] };
    }
  }

  markSavedOpportunities(
    opportunities: OpportunityCard[],
    savedOpportunityIds: string[],
  ): void {
    for (const opportunityId of savedOpportunityIds) {
      const opportunityIndex = opportunities.findIndex(
        (opportunity) => opportunity.key === opportunityId,
      );
      if (opportunityIndex !== -1) {
        opportunities[opportunityIndex].isSaved = true;
      }
    }
  }

  markSavedCases(cases: CaseCard[], savedCaseIds: string[]): void {
    for (const caseId of savedCaseIds) {
      const caseIndex = cases.findIndex((caseItem) => caseItem.key === caseId);
      if (caseIndex !== -1) {
        cases[caseIndex].isSaved = true;
      }
    }
  }

  async performSaveEmail(
    key: string,
    type: EntryType,
  ): Promise<{ success: boolean; documentId?: string; error?: string }> {
    let isSuccessful = false;
    const isCompose = this.isComposeMode;

    try {
      const emlFile = isCompose
        ? await this.outlookService.getSavedComposeEmlFile()
        : await this.outlookService.getReadEmlFile();
      if (!emlFile) throw new Error('Unable to get EML file');

      const documentId = await this.emailService.saveEmailToSingleEntry(
        emlFile,
        key,
        isCompose,
      );

      if (!documentId) {
        return {
          success: false,
          error: 'outlook.emailNotification.failEntry',
        };
      }

      isSuccessful = true;
      await this.outlookService.saveOpportunityOrCaseInOutlook(key, type);

      return { success: true, documentId };
    } catch (error) {
      console.error('Error during saveEmail:', error);
      return {
        success: false,
        error: 'outlook.emailNotification.failEntry',
      };
    } finally {
      this.tracker.trackEvent({
        module: EventModule.Email,
        journey: EventJourney.Save,
        action: EventAction.Completed,
        metadata: {
          result: isSuccessful ? EventMetadata.Success : EventMetadata.Failed,
          entity: type,
          emailDetails: false,
          search: false,
          location: isCompose ? 'compose' : 'read',
        },
      });
    }
  }

  updateSavedEmailStatus(
    entries: OpportunityCard[] | CaseCard[],
    key: string,
  ): void {
    const entry = entries.find((item) => item.key === key);
    if (entry) {
      entry.isSaved = true;
    }
  }
}
